import { PostgrestError } from '@supabase/supabase-js';
import { toast } from 'sonner';

/**
 * Error categories for better error handling and reporting
 */
export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  SERVER = 'server',
  NETWORK = 'network',
  UNKNOWN = 'unknown'
}

/**
 * Structured error object with additional context for error handling
 */
export interface StructuredError {
  message: string;
  category: ErrorCategory;
  originalError: PostgrestError | Error | unknown;
  code?: string;
  details?: string;
  /**
   * Whether this error should be shown to the user
   */
  userVisible: boolean;
  /**
   * Whether the operation can be safely retried
   */
  retryable: boolean;
}

/**
 * Map PostgreSQL error codes to error categories
 * @param code PostgreSQL error code
 * @returns ErrorCategory
 */
function categorizePostgresError(code: string): ErrorCategory {
  // Auth errors
  if (code.startsWith('28')) {
    return ErrorCategory.AUTHORIZATION;
  }
  
  // Data exceptions
  if (code.startsWith('22')) {
    return ErrorCategory.VALIDATION;
  }
  
  // Integrity constraint violations
  if (code.startsWith('23')) {
    return ErrorCategory.CONFLICT;
  }
  
  // Not found
  if (code === 'P0002' || code === '42P01' || code === 'PGRST116') {
    return ErrorCategory.NOT_FOUND;
  }
  
  // Server errors
  if (code.startsWith('53') || code.startsWith('54') || code.startsWith('58') || code.startsWith('XX')) {
    return ErrorCategory.SERVER;
  }
  
  return ErrorCategory.UNKNOWN;
}

/**
 * Get a user-friendly error message for common Supabase errors
 * @param error PostgrestError or any other error
 * @returns User-friendly error message
 */
export function getUserFriendlyMessage(error: PostgrestError | Error | unknown): string {
  if (!error) return 'An unknown error occurred';
  
  // Handle PostgrestError
  if (typeof error === 'object' && error !== null && 'code' in error && 'message' in error) {
    const pgError = error as PostgrestError;
    const code = pgError.code;
    
    // Not found
    if (code === 'PGRST116') {
      return 'The requested record was not found';
    }
    
    // Foreign key violation
    if (code === '23503') {
      return 'This operation would affect related records and cannot be completed';
    }
    
    // Unique violation
    if (code === '23505') {
      return 'A record with this information already exists';
    }
    
    // Check constraint
    if (code === '23514') {
      return 'The data does not meet validation requirements';
    }
    
    // Auth error
    if (code === '42501' || code === '28000') {
      return 'You do not have permission to perform this action';
    }
    
    // Return the message if it exists and seems user-friendly
    const message = pgError.message || '';
    if (message && !message.includes('ERROR') && !message.includes('error') && message.length < 100) {
      return message;
    }
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    // Return the message if it seems user-friendly
    if (!error.message.includes('ERROR') && !error.message.includes('error') && error.message.length < 100) {
      return error.message;
    }
  }
  
  // Default error message
  return 'An unexpected error occurred. Please try again or contact support if the issue persists.';
}

/**
 * Process a Supabase error into a structured format
 * @param error PostgrestError or any other error
 * @returns StructuredError object
 */
export function processSupabaseError(error: PostgrestError | Error | unknown): StructuredError {
  // Handle PostgrestError
  if (typeof error === 'object' && error !== null && 'code' in error && 'message' in error) {
    const pgError = error as PostgrestError;
    const code = pgError.code || '';
    const category = categorizePostgresError(code);
    
    return {
      message: getUserFriendlyMessage(error),
      category,
      originalError: error,
      code,
      details: pgError.details || pgError.hint || undefined,
      userVisible: category !== ErrorCategory.UNKNOWN && category !== ErrorCategory.SERVER,
      retryable: category === ErrorCategory.NETWORK || category === ErrorCategory.SERVER
    };
  }
  
  // Handle network errors
  if (error instanceof Error) {
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      return {
        message: getUserFriendlyMessage(error),
        category: ErrorCategory.NETWORK,
        originalError: error,
        userVisible: true,
        retryable: true
      };
    }
    
    return {
      message: getUserFriendlyMessage(error),
      category: ErrorCategory.UNKNOWN,
      originalError: error,
      userVisible: true,
      retryable: false
    };
  }
  
  // Handle unknown errors
  return {
    message: getUserFriendlyMessage(error),
    category: ErrorCategory.UNKNOWN,
    originalError: error,
    userVisible: true,
    retryable: false
  };
}

/**
 * Log an error with appropriate severity based on the category
 * @param error StructuredError or raw error
 * @param context Additional context about where the error occurred
 */
export function logError(
  error: StructuredError | PostgrestError | Error | unknown,
  context?: string
): void {
  // Convert to StructuredError if needed
  const structuredError = 'category' in (error as any)
    ? error as StructuredError
    : processSupabaseError(error);
  
  const { category, message, originalError } = structuredError;
  const contextPrefix = context ? `[${context}] ` : '';
  
  // Log with appropriate level based on category
  switch (category) {
    case ErrorCategory.NETWORK:
      // Network errors are often transient
      console.warn(`${contextPrefix}Network Error: ${message}`, structuredError);
      break;
    
    case ErrorCategory.AUTHENTICATION:
    case ErrorCategory.AUTHORIZATION:
      // Auth errors usually indicate problems with permissions or session
      console.warn(`${contextPrefix}Auth Error: ${message}`, structuredError);
      break;
    
    case ErrorCategory.VALIDATION:
    case ErrorCategory.CONFLICT:
    case ErrorCategory.NOT_FOUND:
      // Data errors indicate client-side issues
      console.info(`${contextPrefix}Data Error: ${message}`, structuredError);
      break;
    
    case ErrorCategory.SERVER:
    case ErrorCategory.UNKNOWN:
    default:
      // Server and unknown errors are the most severe
      console.error(`${contextPrefix}Error: ${message}`, structuredError);
      break;
  }
  
  // Add analytics or error reporting here if available
  // e.g., Sentry, LogRocket, etc.
}

/**
 * Show an error toast with appropriate message based on the error type
 * @param error StructuredError or raw error
 * @param fallbackMessage Optional fallback message if no user-friendly message can be derived
 */
export function showErrorToast(
  error: StructuredError | PostgrestError | Error | unknown,
  fallbackMessage = 'An error occurred'
): void {
  // Convert to StructuredError if needed
  const structuredError = 'category' in (error as any)
    ? error as StructuredError 
    : processSupabaseError(error);
  
  // Only show toast for user-visible errors
  if (structuredError.userVisible) {
    toast.error(structuredError.message || fallbackMessage);
  } else {
    toast.error(fallbackMessage);
    // Still log the error
    logError(structuredError);
  }
}

/**
 * Safe error handler that handles, logs, and optionally displays an error
 * @param error Any error object
 * @param options Configuration options
 * @returns The structured error object for further handling if needed
 */
export function handleSupabaseError(
  error: PostgrestError | Error | unknown,
  options: {
    context?: string;
    showToast?: boolean;
    toastMessage?: string;
  } = {}
): StructuredError {
  const { context, showToast = true, toastMessage } = options;
  const structuredError = processSupabaseError(error);
  
  // Log the error
  logError(structuredError, context);
  
  // Show toast if requested and the error is user-visible
  if (showToast && structuredError.userVisible) {
    toast.error(toastMessage || structuredError.message);
  }
  
  return structuredError;
} 